import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useContentToken } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Descriptions longer than this (in plain-text characters) collapse behind a
// "Read More" toggle; shorter ones render in full with no toggle.
const DESCRIPTION_MAX_CHARS = 400;

const DESCRIPTION_DEFAULT =
  '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>';

/** Approximate visible character count of a richtext value (tags stripped,
 *  common entities decoded, whitespace collapsed). Deterministic so it matches
 *  between SSR and hydration. */
function plainTextLength(html: string): number {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim().length;
}

const imageDef: SlotGroupDefinition = {
  label: 'Main Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
      suggestedFields: ['property_featured_image', 'photo_gallery[0].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt:
      'Pick an outdoor amenity or building exterior shot — an aerial view, the facade, courtyard, pool deck, or grounds. Avoid low-quality, cluttered, dark, or otherwise undesirable images.',
    context:
      'Large primary image (parallax) in the Property Overview section. Should differ from the smaller secondary image beside it.',
  },
};

const imageSecondaryDef: SlotGroupDefinition = {
  label: 'Secondary Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
      suggestedFields: ['photo_gallery[1].image', 'property_featured_image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community interior' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt:
      'Prefer a photo of an apartment interior — a unit living space, kitchen, or bedroom. If no apartment interior photos exist, fall back to a general community photo. Avoid low-quality, cluttered, dark, or otherwise undesirable images.',
    context:
      'Small framed thumbnail overlapping the larger main image in the Property Overview section. Should differ from the main image.',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const ctaSecondaryDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const image = useSlotGroup('propertyoverview.image', imageDef);
  const imageSecondary = useSlotGroup('propertyoverview.image_secondary', imageSecondaryDef);
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const ctaSecondary = useSlotGroup('propertyoverview.cta_secondary', ctaSecondaryDef);

  // "Read More" for a long description: measure the resolved richtext length so
  // the toggle only appears past the threshold. Done from the raw value (not the
  // DOM) so SSR and client agree and there's no hydration flash.
  const descHtml = useContentToken('propertyoverview.description', {
    defaultField: 'property_long_description',
    defaultValue: DESCRIPTION_DEFAULT,
  });
  const descIsLong = useMemo(
    () => plainTextLength(descHtml || '') > DESCRIPTION_MAX_CHARS,
    [descHtml],
  );
  const [descExpanded, setDescExpanded] = useState(false);

  useRevealStyles();
  const cardRef = useScrollReveal<HTMLDivElement>();

  // Parallax: the main image travels slower than page scroll within its frame.
  const stageRef = useRef<HTMLDivElement>(null);
  const mainParallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = stageRef.current;
    const parallax = mainParallaxRef.current;
    if (!section || !parallax) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      // Image travels 100 px total (±50 px) — the frame is 140% tall so edges
      // never show.
      const offset = (clamped - 0.5) * 100;
      parallax.style.transform = `translateY(${offset}px)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    // Prime an initial offset so the crop is already shifted before scroll.
    tick();

    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) rafId = requestAnimationFrame(tick);
        else cancelAnimationFrame(rafId);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="po-section">
      {/* Decorative right-aligned pattern */}
      <div className="po-pattern" aria-hidden="true" />

      <div ref={stageRef} className="po-stage">
        <div className="po-media">
        {/* Eyebrow box */}
        <div className="po-eyebrow-box">
          <RichTextContent path="propertyoverview.pre_title" as="div" className="po-eyebrow" />
        </div>

        {/* Main aerial image (parallax) */}
        <div className="po-photo po-photo--main">
          <div ref={mainParallaxRef} className="po-parallax">
            <EditableSlotGroup
              groupPath="propertyoverview.image"
              definition={imageDef}
              className="po-parallax-inner"
            >
              {image.image ? (
                <VibesitesImage
                  url={image.image}
                  alt={image.alt || 'Community photo'}
                  className="po-photo-img"
                  sizes="(min-width: 1024px) 52vw, 100vw"
                  objectFit="cover"
                />
              ) : (
                <div className="po-photo-fallback" aria-hidden="true" />
              )}
            </EditableSlotGroup>
          </div>
        </div>

        {/* Secondary interior image */}
        <EditableSlotGroup
          groupPath="propertyoverview.image_secondary"
          definition={imageSecondaryDef}
          className="po-photo po-photo--sub"
        >
          {imageSecondary.image ? (
            <VibesitesImage
              url={imageSecondary.image}
              alt={imageSecondary.alt || 'Community interior'}
              className="po-photo-img"
              sizes="(min-width: 1024px) 22vw, 70vw"
              objectFit="cover"
            />
          ) : (
            <div className="po-photo-fallback" aria-hidden="true" />
          )}
        </EditableSlotGroup>
        </div>

        {/* Content card */}
        <div ref={cardRef} className="po-card reveal-fade-up">
          <RichTextContent path="propertyoverview.lead" as="p" className="po-lead" />
          <RichTextContent path="propertyoverview.title" as="h2" className="po-title" />
          <RichTextContent path="propertyoverview.subtitle" as="p" className="po-subtitle" />
          <div
            className={`po-desc-wrap${descIsLong && !descExpanded ? ' po-desc-wrap--collapsed' : ''}`}
          >
            <RichTextContent path="propertyoverview.description" as="div" className="po-desc" />
          </div>
          {descIsLong && (
            <button
              type="button"
              className="po-readmore"
              aria-expanded={descExpanded}
              onClick={() => setDescExpanded((prev) => !prev)}
            >
              {descExpanded ? 'Read Less' : 'Read More'}
            </button>
          )}

          <div className="po-ctas">
            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="po-btn po-btn--primary"
              >
                {ctaLink.label || 'Explore Floor Plans'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="propertyoverview.cta_secondary" definition={ctaSecondaryDef}>
              <a
                href={ctaSecondary.url || '/schedule-a-tour'}
                target={ctaSecondary._target || '_self'}
                className="po-btn po-btn--outline"
              >
                {ctaSecondary.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .po-section {
          --po-navy: #1b2a4a;
          position: relative;
          overflow: hidden;
          background: var(--vibe-color-background);
          padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 2.5rem);
        }
        .po-stage {
          position: relative;
          max-width: 80rem;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
        }

        /* Decorative right-side pattern */
        .po-pattern {
          display: block;
          position: absolute;
          top: 0;
          bottom: 0;
          right: calc(-1 * clamp(1rem, 4vw, 2.5rem));
          width: 70%;
          z-index: 0;
          background-image: url('https://irp.cdn-website.com//e483d029/dms3rep/multi/opt/menubg-1280w.png');
          background-repeat: no-repeat;
          background-position: top right;
          background-size: cover;
          opacity: 0.9;
          pointer-events: none;
          -webkit-mask-image: linear-gradient(to left, rgba(0,0,0,1) 45%, rgba(0,0,0,0));
          mask-image: linear-gradient(to left, rgba(0,0,0,1) 45%, rgba(0,0,0,0));
        }

        /* Media composition wrapper (mobile = layered anchor) */
        .po-media { position: relative; }

        /* Eyebrow box */
        .po-eyebrow-box {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 3;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          padding: 1.15rem 1.4rem;
          margin: 0;
          width: 58%;
          max-width: 20rem;
        }
        .po-eyebrow {
          font-family: var(--vibe-font-heading);
          font-weight: 300;
          font-size: clamp(1.3rem, 4.5vw, 1.7rem);
          line-height: 1.15;
          text-transform: none;
        }
        .po-eyebrow strong { font-weight: 700; }
        .po-eyebrow p { margin: 0; }

        /* Photos */
        .po-photo {
          position: relative;
          display: block;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        .po-photo--main {
          width: 100%;
          margin: 1.25rem 0 0;
          aspect-ratio: 16 / 9;
        }
        .po-photo--sub {
          /* Hidden by default (phones); shown on tablet and desktop below. */
          display: none;
          position: absolute;
          left: 2%;
          top: 38%;
          width: 46%;
          max-width: 20rem;
          aspect-ratio: 4 / 5;
          margin: 0;
          z-index: 4;
          border: 6px solid #fff;
          box-shadow: var(--vibe-shadow-md, 0 4px 6px rgba(0,0,0,0.08));
        }
        .po-parallax {
          position: absolute;
          top: -20%;
          bottom: -20%;
          left: 0;
          right: 0;
          will-change: transform;
        }
        .po-parallax-inner { position: absolute; inset: 0; }
        .po-photo-img,
        .po-photo-fallback {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .po-photo-fallback {
          background: linear-gradient(135deg, var(--vibe-color-surface) 0%, var(--vibe-color-border) 100%);
        }

        /* Card */
        .po-card {
          order: 4;
          position: relative;
          z-index: 3;
          width: 100%;
          background: var(--vibe-color-background);
          box-shadow: 0 18px 40px rgba(27, 42, 74, 0.14);
          border: 1px solid var(--vibe-color-border);
          padding: clamp(1.75rem, 5vw, 3rem);
          margin-top: -3rem;
          text-align: center;
        }
        .po-lead {
          margin: 0 0 0.35rem;
          font-family: var(--vibe-font-body);
          font-size: clamp(1rem, 2.4vw, 1.35rem);
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }
        .po-title {
          margin: 0 0 0.6rem;
          font-family: var(--vibe-font-heading);
          font-weight: 700;
          font-size: clamp(2.5rem, 7vw, 4rem);
          line-height: 0.95;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        .po-title p { margin: 0; }
        .po-subtitle {
          margin: 0 0 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: clamp(0.85rem, 1.6vw, 1.05rem);
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }
        .po-subtitle p { margin: 0; }
        .po-desc {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.625;
          color: var(--vibe-color-text);
        }
        .po-desc p { margin: 0 0 0.85rem; }
        .po-desc a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        /* Read More: collapse a long description behind a fade + toggle. */
        .po-desc-wrap { position: relative; }
        .po-desc-wrap--collapsed {
          max-height: 11rem;
          overflow: hidden;
        }
        .po-desc-wrap--collapsed::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3.75rem;
          background: linear-gradient(
            to bottom,
            transparent,
            var(--vibe-color-background)
          );
          pointer-events: none;
        }
        .po-readmore {
          display: inline-block;
          margin-top: 0.85rem;
          padding: 0;
          background: transparent;
          border: none;
          cursor: pointer;
          font-family: var(--vibe-font-body);
          font-size: 0.76rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 0.2s ease;
        }
        .po-readmore:hover {
          color: color-mix(in srgb, var(--vibe-color-primary) 80%, #000000);
        }

        /* CTAs */
        .po-ctas {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          margin-top: 1.75rem;
        }
        .po-ctas > * {
          min-width: 0;
        }
        @media (min-width: 640px) {
          .po-ctas {
            flex-direction: row;
            flex-wrap: nowrap;
          }
          .po-ctas > * {
            flex: 1 1 0;
          }
        }
        .po-btn {
          width: 100%;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem clamp(0.75rem, 3vw, 1.9rem);
          font-family: var(--vibe-font-body);
          font-size: 0.76rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          white-space: nowrap;
          border: 1px solid transparent;
          transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease, transform 0.2s ease;
        }
        .po-btn:hover { transform: translateY(-2px); }
        .po-btn--primary {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }
        .po-btn--primary:hover { background: color-mix(in srgb, var(--vibe-color-primary) 85%, #000000); }
        .po-btn--outline {
          background: transparent;
          color: var(--po-navy);
          border-color: color-mix(in srgb, var(--po-navy) 45%, #ffffff);
        }
        .po-btn--outline:hover { background: color-mix(in srgb, var(--po-navy) 6%, #ffffff); border-color: var(--po-navy); }

        /* Card content is centered on phones; restore left alignment from
           tablet up where the layout has more horizontal room. */
        @media (min-width: 768px) {
          .po-card { text-align: left; }
          .po-desc { font-size: 1.125rem; }
        }

        /* ── Tablet (768–1023px): same stacked layout as mobile, just a
              wider, shorter main image so the "Come Home" card sits close
              to the eyebrow box, with the secondary thumbnail resting
              fully on top of the main image. ──────────────────────── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .po-photo--main {
            /* A full-width image is very tall at tablet widths — cap it so
               the text content isn't pushed far down the page. */
            aspect-ratio: 21 / 9;
            max-height: 20rem;
          }
          /* Deepen the card overlap a touch so the text is pulled up nearer
             the eyebrow box. */
          .po-card {
            margin-top: -4rem;
          }
          /* Secondary thumbnail: shown on tablet, resting on the left side of
             the main image and vertically centered against it. */
          .po-photo--sub {
            display: block;
            left: 1.25rem;
            /* Center the thumbnail within the *visible* image. The card
               overlaps the bottom of the image, so a small upward nudge
               balances the spacing above and below in the exposed photo. */
            top: calc(50% - 1rem);
            bottom: auto;
            transform: translateY(-50%);
            width: 40%;
            max-width: 18rem;
            aspect-ratio: 3 / 2;
          }
        }

        /* ── Desktop (1024px+): layered composition — eyebrow top-left, a
              tall parallax image, the secondary thumbnail beside it, and the
              text card centered against the full-height image. ─────── */
        @media (min-width: 1024px) {
          .po-stage {
            display: flex;
            flex-direction: row;
            align-items: center;
            min-height: 40rem;
          }
          .po-media { display: contents; }

          .po-pattern {
            width: 50%;
          }

          .po-eyebrow-box {
            position: absolute;
            top: 6%;
            left: 0;
            right: auto;
            z-index: 2;
            margin: 0;
            width: 20%;
            max-width: none;
            padding: clamp(1.25rem, 2vw, 2rem);
          }

          .po-photo--main {
            position: absolute;
            top: -2.75rem;
            bottom: -2.75rem;
            left: 4%;
            width: 56%;
            aspect-ratio: auto;
            max-height: none;
            margin: 0;
            z-index: 1;
          }
          .po-photo--sub {
            display: block;
            position: absolute;
            left: 2%;
            top: calc(6% + 11.5rem);
            bottom: auto;
            width: 23%;
            max-width: none;
            height: 42%;
            aspect-ratio: auto;
            margin: 0;
            z-index: 2;
          }

          .po-card {
            width: 46%;
            margin: 0 7% 0 auto;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .po-btn:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Box',
      type: 'richtext',
      defaultValue: '<p>Experience<br><strong>Elevated</strong><br>Living</p>',
    },
    'propertyoverview.lead': {
      label: 'Lead-in',
      type: 'richtext',
      defaultValue: '<p>Come Home To</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'propertyoverview.subtitle': {
      label: 'Location Line',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.image': imageDef,
    'propertyoverview.image_secondary': imageSecondaryDef,
    'propertyoverview.cta_link': ctaLinkDef,
    'propertyoverview.cta_secondary': ctaSecondaryDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
